import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { addRecentSearch, getRecentSearches, removeRecentSearch, clearRecentSearches } from "../lib/recentSearches";
import BusinessCard from "../components/BusinessCard";
import { Sparkle, MapPin, MagnifyingGlass, CaretLeft, CaretRight, Buildings, Clock, X } from "@phosphor-icons/react";

const PAGE_SIZE = 24;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const skip = (page - 1) * PAGE_SIZE;
  const { t } = useI18n();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(q);
  const [recent, setRecent] = useState(() => getRecentSearches());

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => { setInputValue(q); }, [q]);

  useEffect(() => {
    if (!q) return;
    // Record the search once per landing (only page=1, so we don't spam history on pagination)
    if (page === 1) {
      addRecentSearch(q);
      setRecent(getRecentSearches());
    }
    setLoading(true);
    api
      .get("/public/businesses", { params: { q, limit: PAGE_SIZE, skip, sort: "relevance" } })
      .then((r) => {
        setResults(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [q, skip]);

  const goToPage = (n) => {
    const clamped = Math.max(1, Math.min(totalPages, n));
    setParams({ q, page: String(clamped) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const cleaned = inputValue.trim();
    if (!cleaned) return;
    navigate(`/recherche?q=${encodeURIComponent(cleaned)}`);
  };

  const goToRecent = (query) => {
    navigate(`/recherche?q=${encodeURIComponent(query)}`);
  };

  const dropRecent = (query, e) => {
    e.stopPropagation();
    removeRecentSearch(query);
    setRecent(getRecentSearches());
  };

  const wipeRecent = () => {
    clearRecentSearches();
    setRecent([]);
  };

  const pageNumbers = useMemo(() => paginationRange(page, totalPages), [page, totalPages]);

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-10" data-testid="search-page">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Accueil</Link>

      {/* Header + inline search bar so users can refine without going back */}
      <div className="mt-5 mb-8">
        <div className="label-uppercase mb-2 inline-flex items-center gap-2">
          <Sparkle size={12} weight="fill" className="text-secondary" /> Recherche
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-4" data-testid="search-query">
          {q ? <>&laquo;&nbsp;{q}&nbsp;&raquo;</> : "Rechercher"}
        </h1>
        <form onSubmit={onSubmit} className="flex items-center gap-2 max-w-2xl" data-testid="search-refine-form">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="coiffeur mulhouse, comptable colmar…"
              className="w-full border border-border rounded-sm pl-9 pr-3 py-2.5 text-sm bg-background focus:border-primary outline-none"
              data-testid="search-refine-input"
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2.5 rounded-sm text-sm font-semibold whitespace-nowrap"
            data-testid="search-refine-submit"
          >
            Chercher
          </button>
        </form>

        {q && !loading && total > 0 && (
          <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-2" data-testid="search-count">
            <Buildings size={12}/>
            <strong className="text-foreground">{total.toLocaleString("fr-FR")}</strong>&nbsp;résultat{total > 1 ? "s" : ""}
            {totalPages > 1 && <> — page {page} sur {totalPages}</>}
          </div>
        )}

        {recent.length > 0 && (
          <div className="mt-4" data-testid="recent-searches">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={12} className="text-muted-foreground"/>
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Recherches récentes</span>
              <button
                type="button"
                onClick={wipeRecent}
                className="text-[10px] text-muted-foreground hover:text-destructive ml-auto"
                data-testid="recent-clear-all"
              >
                Tout effacer
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recent.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => goToRecent(r)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                    r.toLowerCase() === q.toLowerCase()
                      ? "bg-primary/10 border-primary/40 text-primary"
                      : "bg-card border-border hover:bg-accent text-foreground"
                  }`}
                  data-testid={`recent-chip-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  title="Relancer cette recherche"
                >
                  <span className="font-medium">{r}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => dropRecent(r, e)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && dropRecent(r, e)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Retirer ${r}`}
                  >
                    <X size={11} weight="bold"/>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground py-8">Recherche…</div>
      ) : q && results.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-sm p-12 text-center text-muted-foreground" data-testid="search-empty">
          Aucun résultat pour &laquo;&nbsp;{q}&nbsp;&raquo;.<br/>
          <span className="text-xs">Essaie avec un mot-clé plus court, ou une autre orthographe.</span>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results">
            {results.map((b) => <BusinessCard key={b.id} b={b} />)}
          </div>

          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageNumbers={pageNumbers}
              onGoto={goToPage}
            />
          )}
        </>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, pageNumbers, onGoto }) {
  return (
    <nav
      className="mt-12 flex flex-wrap items-center justify-center gap-1.5"
      data-testid="pagination"
      aria-label="Pagination"
    >
      <button
        onClick={() => onGoto(page - 1)}
        disabled={page === 1}
        className="inline-flex items-center gap-1 px-3 py-2 border border-border rounded-sm text-sm font-medium bg-card hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="page-prev"
        aria-label="Page précédente"
      >
        <CaretLeft size={12} weight="bold"/> Préc.
      </button>
      {pageNumbers.map((n, i) =>
        n === "..." ? (
          <span key={`gap-${i}`} className="px-2 text-muted-foreground select-none">…</span>
        ) : (
          <button
            key={n}
            onClick={() => onGoto(n)}
            className={`min-w-[36px] h-[36px] px-2 border rounded-sm text-sm font-semibold tabular-nums ${
              n === page
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border hover:bg-accent"
            }`}
            data-testid={`page-${n}`}
            aria-current={n === page ? "page" : undefined}
          >
            {n}
          </button>
        )
      )}
      <button
        onClick={() => onGoto(page + 1)}
        disabled={page === totalPages}
        className="inline-flex items-center gap-1 px-3 py-2 border border-border rounded-sm text-sm font-medium bg-card hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="page-next"
        aria-label="Page suivante"
      >
        Suiv. <CaretRight size={12} weight="bold"/>
      </button>
    </nav>
  );
}

/**
 * Compact pagination range: [1, 2, 3, ..., 47] style.
 * Always shows: current ± 1, first, last, ellipses.
 */
function paginationRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const range = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...range].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const withGaps = [];
  for (let i = 0; i < sorted.length; i++) {
    withGaps.push(sorted[i]);
    if (sorted[i + 1] && sorted[i + 1] - sorted[i] > 1) withGaps.push("...");
  }
  return withGaps;
}
